import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, MapPin } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageBanner } from "@/components/PageBanner";
import { fetchTravelArticleBySlug, getDefaultThumbnail } from "@/lib/strapi";

export default function TravelArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const fallbackThumbnail = getDefaultThumbnail();

  const handleImageError = (event) => {
    if (event.currentTarget.src !== fallbackThumbnail) {
      event.currentTarget.src = fallbackThumbnail;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadArticle = async () => {
      try {
        setIsLoading(true);
        setError("");
        const data = await fetchTravelArticleBySlug(slug);
        if (mounted) {
          setArticle(data);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : "Failed to load travel article");
          setArticle(null);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadArticle();

    return () => {
      mounted = false;
    };
  }, [slug]);

  return (
    <div data-testid="travel-article-page" className="min-h-screen">
      <Header />

      {isLoading && (
        <section className="pt-[72px] bg-white min-h-[50vh]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 text-warm-gray font-inter text-sm">
            Loading travel article...
          </div>
        </section>
      )}

      {!isLoading && !!error && (
        <section className="pt-[72px] bg-white min-h-[50vh]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-20">
            <p className="font-inter text-sm text-red-600 mb-6">Unable to load this travel article right now.</p>
            <Link to="/travel" className="inline-flex items-center gap-2 font-inter text-sm text-amber-dusk">
              <ArrowLeft size={14} />
              Back to Travel
            </Link>
          </div>
        </section>
      )}

      {!isLoading && !error && article && (
        <>
          <PageBanner
            eyebrow={article.category || "Swim-Travel"}
            title={article.title}
            description={article.teaser}
            image={article.image}
          />

          <section className="bg-white py-16 md:py-20">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <div className="flex flex-wrap items-center gap-4 mb-8">
                <Link
                  to="/travel"
                  className="inline-flex items-center gap-2 font-inter text-sm text-amber-dusk hover:text-solar-orange transition-colors"
                >
                  <ArrowLeft size={14} />
                  Back to Travel
                </Link>
                {!!article.location && (
                  <div className="inline-flex items-center gap-2 text-warm-gray">
                    <MapPin size={12} />
                    <span className="font-inter font-light text-xs">{article.location}</span>
                  </div>
                )}
                {!!article.date && (
                  <div className="inline-flex items-center gap-2 text-warm-gray">
                    <Clock size={12} />
                    <span className="font-inter font-light text-xs">{article.date}</span>
                  </div>
                )}
              </div>

              <div className="relative overflow-hidden rounded-sm aspect-[16/9] mb-8 border border-near-black/10">
                <img
                  src={article.image}
                  alt={article.title}
                  loading="lazy"
                  onError={handleImageError}
                  className="w-full h-full object-cover"
                />
              </div>

              {!!article.body ? (
                <article
                  className="article-content"
                  dangerouslySetInnerHTML={{ __html: article.body }}
                />
              ) : (
                <p className="font-inter text-base text-warm-gray leading-relaxed">{article.teaser}</p>
              )}
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
}
